import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  recipesApi,
  Recipe,
  RecipeListParams,
  RecipeCreateInput,
  RecipeUpdateInput,
  Ingredient,
  Instruction,
} from '../api/recipes';
import { useToast } from '../components/ui/Toast';

// Query keys
export const recipeKeys = {
  all: ['recipes'] as const,
  lists: () => [...recipeKeys.all, 'list'] as const,
  list: (filters: RecipeListParams) => [...recipeKeys.lists(), filters] as const,
  search: (query: string) => [...recipeKeys.all, 'search', query] as const,
  details: () => [...recipeKeys.all, 'detail'] as const,
  detail: (id: string) => [...recipeKeys.details(), id] as const,
};

export function useRecipes(params: RecipeListParams = {}) {
  return useQuery({
    queryKey: recipeKeys.list(params),
    queryFn: () => recipesApi.list(params),
  });
}

export function useRecipeSearch(query: string) {
  return useQuery({
    queryKey: recipeKeys.search(query),
    queryFn: () => recipesApi.search(query),
    enabled: query.length > 0,
  });
}

export function useRecipe(id: string) {
  return useQuery({
    queryKey: recipeKeys.detail(id),
    queryFn: () => recipesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateRecipe() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: RecipeCreateInput) => recipesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      showToast('Recipe created!', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create recipe', 'error');
    },
  });
}

export function useUpdateRecipe() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecipeUpdateInput }) => recipesApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      showToast('Recipe updated!', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update recipe', 'error');
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => recipesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      showToast('Recipe deleted', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete recipe', 'error');
    },
  });
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipesApi.toggleFavorite(id),
    onMutate: async id => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: recipeKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: recipeKeys.lists() });

      // Snapshot previous value
      const previousRecipe = queryClient.getQueryData<Recipe>(recipeKeys.detail(id));
      const previousLists = queryClient.getQueriesData({
        queryKey: recipeKeys.lists(),
      });

      // Optimistically update the cache
      if (previousRecipe) {
        queryClient.setQueryData<Recipe>(recipeKeys.detail(id), {
          ...previousRecipe,
          is_favorite: !previousRecipe.is_favorite,
        });
      }

      // Update lists cache
      previousLists.forEach(([queryKey, data]) => {
        if (Array.isArray(data)) {
          queryClient.setQueryData(
            queryKey,
            data.map((recipe: Recipe) => (recipe.id === id ? { ...recipe, is_favorite: !recipe.is_favorite } : recipe)),
          );
        }
      });

      return { previousRecipe, previousLists };
    },
    onError: (_, id, context) => {
      // Rollback on error
      if (context?.previousRecipe) {
        queryClient.setQueryData(recipeKeys.detail(id), context.previousRecipe);
      }
      context?.previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}

export function useUpdateIngredients() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ingredients }: { id: string; ingredients: Ingredient[] }) =>
      recipesApi.updateIngredients(id, ingredients),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.detail(id) });
    },
  });
}

export function useUpdateInstructions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, instructions }: { id: string; instructions: Instruction[] }) =>
      recipesApi.updateInstructions(id, instructions),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.detail(id) });
    },
  });
}
